import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Input, Label, PageShell, Select, SectionCard } from '@/components/ui';
import { ArtistStorefrontLinks } from '../components/ArtistStorefrontLinks';
import { buildStorefrontArtistUrl } from '../lib/storefrontArtistUrl';
import { ApiClientError } from '@/lib/http-core';
import type { Artist } from '@/lib/api-admin/types';
import {
  ARTIST_GENDERS,
  artistUpdateSchema,
  type ArtistUpdateValues,
} from '../schemas/artistFormSchema';
import { useArtistQuery } from '../hooks/useArtistQuery';
import {
  useArchiveArtistMutation,
  usePurgeArtistMutation,
  useUpdateArtistMutation,
} from '../hooks/useArtistMutation';
import {
  useCreateArtistAliasMutation,
  useDeleteArtistAliasMutation,
} from '../hooks/useArtistAliasMutation';
import {
  useDeleteArtistImageMutation,
  useUploadArtistImageMutation,
} from '../hooks/useArtistImageMutation';
import {
  useAddArtistMemberMutation,
  useRemoveArtistMemberMutation,
} from '../hooks/useArtistMemberMutation';
import { ArtistStatusPill } from '../components/ArtistStatusPill';
import { ArtistTypeBadge } from '../components/ArtistTypeBadge';
import { ArtistAliasManager } from '../components/ArtistAliasManager';
import { ArtistImageUploader } from '../components/ArtistImageUploader';
import { ArtistMemberManager } from '../components/ArtistMemberManager';

/**
 * QA-380 — 그룹 아티스트의 [그룹 멤버] 등록 화면 숨김. 자사몰에서 지금은 멤버 연결이 필요 없어 화면에서만 감춘다.
 * 기능(멤버 추가·제외 API, ArtistMemberManager)은 그대로 두었다 — 다시 쓰려면 true 로 바꾸면 된다.
 */
export const ARTIST_GROUP_MEMBERS_ENABLED = false;
import { ArtistArchiveModal } from '../components/ArtistArchiveModal';
import { ArtistDeleteModal } from '../components/ArtistDeleteModal';
import { ArtistDisplayedBadge } from '../components/ArtistDisplayedBadge';
import { artistDisplayedOf } from '../lib/artistDisplayed';

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) return err.message ?? err.code;
  if (err instanceof Error) return err.message;
  return null;
};

const stripEmpty = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== '' && v !== undefined && v !== null) (out as Record<string, unknown>)[k] = v;
  }
  return out;
};

const toUpdateDefaults = (artist: Artist): ArtistUpdateValues => ({
  stageName: artist.stageName,
  legalName: artist.legalName ?? '',
  gender: artist.gender ?? null,
  nationality: artist.nationality ?? '',
  debutDate: artist.debutDate ?? '',
  birthDate: artist.birthDate ?? '',
  fanClubName: artist.fanClubName ?? '',
  agencyName: artist.agencyName ?? '',
  bio: artist.bio ?? '',
});

export const ArtistDetailPage = () => {
  const { t } = useTranslation('artists');
  const navigate = useNavigate();
  const params = useParams<{ artistId: string }>();
  const artistId = params.artistId;

  const query = useArtistQuery(artistId);
  const update = useUpdateArtistMutation();
  const archive = useArchiveArtistMutation();
  const purge = usePurgeArtistMutation();
  const createAlias = useCreateArtistAliasMutation();
  const deleteAlias = useDeleteArtistAliasMutation();
  const uploadImage = useUploadArtistImageMutation();
  const deleteImage = useDeleteArtistImageMutation();
  const addMember = useAddArtistMemberMutation();
  const removeMember = useRemoveArtistMemberMutation();

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);

  const formValues = query.data ? toUpdateDefaults(query.data) : undefined;
  const form = useForm<ArtistUpdateValues>({
    resolver: zodResolver(artistUpdateSchema),
    defaultValues: formValues ?? {},
    ...(formValues ? { values: formValues } : {}),
    mode: 'onBlur',
  });

  if (query.isPending) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="py-12 text-center text-sm text-[color:var(--content-muted)]"
      >
        {t('artists.detail.loading')}
      </div>
    );
  }

  if (query.isError || !query.data || !artistId) {
    return (
      <div
        role="alert"
        aria-live="polite"
        className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
        style={{
          background: 'var(--status-danger-bg)',
          color: 'var(--status-danger-fg)',
        }}
      >
        <p>{t('artists.detail.error')}</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/artists')}>
          {t('artists.actions.backToList')}
        </Button>
      </div>
    );
  }

  const artist: Artist = query.data;
  const isGroup = artist.type === 'GROUP';
  const updateError = errorMessageOf(update.error);
  const archiveError = errorMessageOf(archive.error);
  const purgeError = errorMessageOf(purge.error);
  const aliasError = errorMessageOf(createAlias.error ?? deleteAlias.error);
  const imageError = errorMessageOf(uploadImage.error ?? deleteImage.error);
  const memberError = errorMessageOf(addMember.error ?? removeMember.error);

  const onSubmit = (values: ArtistUpdateValues) => {
    const body = stripEmpty(values) as Record<string, unknown>;
    if (Object.keys(body).length === 0) return;
    update.mutate({ artistId, body });
  };

  return (
    <PageShell
      title={
        <span className="flex flex-wrap items-center gap-2">
          {artist.stageName}
          <ArtistStatusPill status={artist.status} artistId={artist.artistId} />
          <ArtistTypeBadge type={artist.type} />
          {/* QA-593 쇼핑몰 노출 중이면 [노출]. */}
          <ArtistDisplayedBadge displayed={artistDisplayedOf(artist)} />
        </span>
      }
      breadcrumb={[
        { label: t('artists.list.title'), href: '/artists' },
        { label: artist.stageName },
      ]}
      actions={
        // 246: 활성 아티스트는 비활성(소프트 삭제) 버튼, 비활성 아티스트는 재활성 버튼을 노출한다.
        // 재활성은 비파괴적 작업이라 기존 useUpdateArtistMutation 으로 PATCH {status:'ACTIVE'} 전송.
        // 영구 삭제(hard delete) 버튼은 상태와 무관하게 archive/activate 옆에 별도 노출한다.
        <div className="flex items-center gap-2">
          {/* QA-562 쇼핑몰 아티스트 페이지 새 창(짧은 주소). */}
          <a
            href={buildStorefrontArtistUrl(artist)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-8 items-center rounded-md border px-3 text-sm"
            style={{ borderColor: 'var(--divider-strong)', color: 'var(--content-strong)' }}
            data-testid="artist-storefront-view"
          >
            쇼핑몰에서 보기
          </a>
          {/* QA-593 쇼핑몰 노출/숨김 — 노출 중이면 [숨김], 숨김이면 [노출]. BE 가 displayed 를 주기 전에는 숨긴다. */}
          {artistDisplayedOf(artist) !== undefined ? (
            <Button
              variant="outline"
              size="sm"
              disabled={update.isPending}
              data-testid="artist-displayed-toggle"
              onClick={() =>
                update.mutate({
                  artistId,
                  body: { displayed: !artistDisplayedOf(artist) } as unknown as Parameters<typeof update.mutate>[0]['body'],
                })
              }
            >
              {artistDisplayedOf(artist) ? '숨김' : '노출'}
            </Button>
          ) : null}
          {artist.status === 'ACTIVE' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setArchiveOpen(true)}
              disabled={archive.isPending}
              data-testid="artist-archive-btn"
            >
              {t('artists.actions.archive')}
            </Button>
          ) : (
            <Button
              variant="cta"
              size="sm"
              onClick={() => update.mutate({ artistId, body: { status: 'ACTIVE' } })}
              disabled={update.isPending}
              data-testid="artist-activate-btn"
            >
              {update.isPending ? t('artists.detail.submitting') : t('artists.actions.activate')}
            </Button>
          )}
          <Button
            variant="danger"
            size="sm"
            onClick={() => setPurgeOpen(true)}
            disabled={purge.isPending}
            data-testid="artist-purge-btn"
          >
            {t('artists.actions.delete')}
          </Button>
        </div>
      }
    >

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard title={t('artists.detail.sectionInfo')}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="grid grid-cols-1 gap-3 md:grid-cols-2"
            aria-label={t('artists.detail.formLabel')}
            noValidate
          >
            <div className="flex flex-col gap-1">
              <Label htmlFor="edit-stageName">{t('artists.fields.stageName')}</Label>
              <Input
                id="edit-stageName"
                {...form.register('stageName')}
                maxLength={100}
                style={{ height: 'var(--density-compact-form-h)' }}
              />
            </div>
            {!isGroup ? (
              <div className="flex flex-col gap-1">
                <Label htmlFor="edit-legalName">{t('artists.fields.legalName')}</Label>
                <Input
                  id="edit-legalName"
                  {...form.register('legalName')}
                  maxLength={100}
                  data-pii="true"
                  style={{ height: 'var(--density-compact-form-h)' }}
                />
              </div>
            ) : null}
            {!isGroup ? (
              <div className="flex flex-col gap-1">
                <Label htmlFor="edit-gender">{t('artists.fields.gender')}</Label>
                <Select
                  id="edit-gender"
                  {...form.register('gender')}
                  style={{ height: 'var(--density-compact-form-h)' }}
                >
                  <option value="">—</option>
                  {ARTIST_GENDERS.map((opt) => (
                    <option key={opt} value={opt}>
                      {t(`artists.gender.${opt}`)}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
            <div className="flex flex-col gap-1">
              <Label htmlFor="edit-nationality">{t('artists.fields.nationality')}</Label>
              <Input
                id="edit-nationality"
                {...form.register('nationality')}
                maxLength={80}
                style={{ height: 'var(--density-compact-form-h)' }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="edit-debutDate">{t('artists.fields.debutDate')}</Label>
              <Input
                id="edit-debutDate"
                type="date"
                {...form.register('debutDate')}
                style={{ height: 'var(--density-compact-form-h)' }}
              />
            </div>
            {!isGroup ? (
              <div className="flex flex-col gap-1">
                <Label htmlFor="edit-birthDate">{t('artists.fields.birthDate')}</Label>
                <Input
                  id="edit-birthDate"
                  type="date"
                  {...form.register('birthDate')}
                  data-pii="true"
                  style={{ height: 'var(--density-compact-form-h)' }}
                />
              </div>
            ) : null}
            <div className="flex flex-col gap-1">
              <Label htmlFor="edit-fanClubName">{t('artists.fields.fanClubName')}</Label>
              <Input
                id="edit-fanClubName"
                {...form.register('fanClubName')}
                maxLength={80}
                style={{ height: 'var(--density-compact-form-h)' }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="edit-agencyName">{t('artists.fields.agencyName')}</Label>
              <Input
                id="edit-agencyName"
                {...form.register('agencyName')}
                maxLength={100}
                style={{ height: 'var(--density-compact-form-h)' }}
              />
            </div>
            <div className="col-span-full flex flex-col gap-1">
              <Label htmlFor="edit-bio">{t('artists.fields.bio')}</Label>
              <textarea
                id="edit-bio"
                {...form.register('bio')}
                maxLength={4000}
                rows={4}
                className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
                style={{ borderColor: 'var(--content-muted)' }}
              />
            </div>
            {updateError ? (
              <p
                role="alert"
                aria-live="polite"
                className="col-span-full rounded-md p-2 text-sm"
                style={{
                  background: 'var(--status-danger-bg)',
                  color: 'var(--status-danger-fg)',
                }}
              >
                {updateError}
              </p>
            ) : null}
            <div className="col-span-full flex justify-end gap-2 pt-2">
              <Button type="submit" variant="cta" size="sm" disabled={update.isPending}>
                {update.isPending
                  ? t('artists.detail.submitting')
                  : t('artists.actions.save')}
              </Button>
            </div>
          </form>
        </SectionCard>

        {/* QA-561/562 4몰 짧은 주소 — SNS 업로드용 복사. */}
        <SectionCard title="쇼핑몰 링크">
          <ArtistStorefrontLinks artistId={artist.artistId} stageName={artist.stageName} />
        </SectionCard>

        <SectionCard title={t('artists.detail.sectionAliases')}>
          <ArtistAliasManager
            aliases={artist.aliases}
            isAdding={createAlias.isPending}
            isDeleting={deleteAlias.isPending}
            errorMessage={aliasError}
            onAdd={({ language, aliasValue, isPrimary }) =>
              createAlias.mutate({
                artistId,
                body: { language, aliasValue, isPrimary },
              })
            }
            onDelete={(aliasId) => deleteAlias.mutate({ artistId, aliasId })}
          />
        </SectionCard>

        {/* 등록 화면과 같이 이미지는 전체 폭(종전엔 반 칸이라 좁았다). */}
        <SectionCard title={t('artists.detail.sectionImages')} className="lg:col-span-2">
          <ArtistImageUploader
            images={artist.images}
            isUploading={uploadImage.isPending}
            isDeleting={deleteImage.isPending}
            errorMessage={imageError}
            onUpload={({ file, type, altText }) =>
              uploadImage.mutate(
                altText
                  ? { artistId, file, type, altText }
                  : { artistId, file, type },
              )
            }
            onDelete={(imageId) => deleteImage.mutate({ artistId, imageId })}
          />
        </SectionCard>

        {ARTIST_GROUP_MEMBERS_ENABLED && isGroup ? (
          <SectionCard title={t('artists.detail.sectionMembers')}>
            <ArtistMemberManager
              members={artist.members ?? []}
              isAdding={addMember.isPending}
              isRemoving={removeMember.isPending}
              errorMessage={memberError}
              onAdd={(input) =>
                addMember.mutate({
                  groupId: artistId,
                  body: input,
                })
              }
              onRemove={(memberLinkId, leftAt) =>
                removeMember.mutate({
                  groupId: artistId,
                  memberLinkId,
                  ...(leftAt ? { leftAt } : {}),
                })
              }
            />
          </SectionCard>
        ) : null}
      </div>

      <ArtistArchiveModal
        isOpen={archiveOpen}
        artistName={artist.stageName}
        productCount={artist.productCount ?? 0}
        isSubmitting={archive.isPending}
        errorMessage={archiveError}
        onCancel={() => {
          setArchiveOpen(false);
          archive.reset();
        }}
        onConfirm={() => {
          archive.mutate(artistId, {
            onSuccess: () => {
              setArchiveOpen(false);
            },
          });
        }}
      />

      <ArtistDeleteModal
        isOpen={purgeOpen}
        artistName={artist.stageName}
        productCount={artist.productCount ?? 0}
        isSubmitting={purge.isPending}
        errorMessage={purgeError}
        onCancel={() => {
          setPurgeOpen(false);
          purge.reset();
        }}
        onConfirm={() => {
          purge.mutate(artistId, {
            onSuccess: () => {
              setPurgeOpen(false);
              navigate('/artists');
            },
          });
        }}
      />
    </PageShell>
  );
};
